import { defineStore } from 'pinia';
import { computed, onMounted, readonly, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';

import { isAdminRole, Role, UpdateUser, User } from '../models/User';
import { RegisterPayload, userService } from '../services/UserService';
import { logError } from '../utils/logger';
import { CreateMembership, FullMembership } from '../models/Membership';
import { createMembership, getMembership } from '../services/api/membershipsService';
import { UnauthenticatedError } from '../error/UnathenticatedError';
import { useRouter } from 'vue-router';
import { useNotifications } from '../composition/useNotifications';
import { Route } from '../routes/routes';
import { slugify } from '../utils/slugify';
import { client, EdgeFunctionError } from '../services/api/client';

const mockRoleKey = 'mockRole';

// Dev-only role override read from localStorage. Returns null in production
// builds so the override is unreachable from a shipped bundle — Vite strips
// the `import.meta.env.DEV` branch via dead-code elimination at build time.
// Vitest runs with `DEV = true`, so tests using `mockRole` (see test/testEnv.ts)
// keep working.
export function getMockRole(): Role | null {
    if (!import.meta.env.DEV) {
        return null;
    }
    return localStorage.getItem(mockRoleKey) as Role | null;
}

export const useUserStore = defineStore('user', () => {
    const router = useRouter();
    const notif = useNotifications();

    const user = ref<User>();
    const membership = ref<FullMembership | null>(null);
    const isLoading = ref(true);

    const userPromiseResolver = ref<(value?: unknown) => void>();
    const isUserResolvedPromise = ref(new Promise((resolve) => {
        userPromiseResolver.value = resolve;
    }));
    
    const isLoggedIn = computed(() => user.value != null);
    const isNonProfitMember = computed(() => membership.value != null);

    const role = computed(() => {
        if (getMockRole()) {
            return getMockRole();
        } else {
            return user.value?.role;
        }
    })

    async function login(email: string, password: string) {
        try {
            const loggedInUser = await userService.login(email, password);

            if (loggedInUser) {
                user.value = loggedInUser;
                membership.value = await getMembership();
            } else {
                throw new Error('Invalid credentials');
            }
        } catch (error) {
            // Wrong-password / unknown-username is a routine user mistake, not a
            // bug. The UI already surfaces it; sending it to Sentry just buries
            // the real failures under bad-creds noise.
            if (!isExpectedLoginFailure(error)) {
                logError(error);
            }
            throw error;
        }
    }

    function isExpectedLoginFailure(error: unknown): boolean {
        return error instanceof EdgeFunctionError
            && error.functionName === 'username-auth'
            && error.status >= 400
            && error.status < 500;
    }

    async function loginWithDiscord() {
        return userService.loginWithDiscord();
    }

    async function loginWithPasskey() {
        try {
            const loggedInUser = await userService.loginWithPasskey();
            user.value = loggedInUser;
            membership.value = await getMembership();
            return loggedInUser;
        } catch (error) {
            // Cancelled biometric prompts and edge-function "Login failed"
            // results are routine UX (user tapped wrong device, dismissed
            // Face ID, no enrolled credential). The LoginModal already
            // surfaces them via flow analytics; only log the unexpected.
            if (!isExpectedPasskeyLoginFailure(error)) {
                logError(error);
            }
            throw error;
        }
    }

    function isExpectedPasskeyLoginFailure(error: unknown): boolean {
        if (error instanceof Error && error.name === 'PasskeyLoginCancelledError') {
            return true;
        }
        return error instanceof EdgeFunctionError
            && error.functionName === 'webauthn-login'
            && error.status >= 400
            && error.status < 500;
    }

    async function resetPassword(email: string) {
        if (user.value != null) {
            throw new Error('You are already logged in');
        }

        if (!email) {
            throw new Error('Email is required');
        }

        try {
            await userService.sendPasswordReset(email);
        } catch (error) {
            logError(error);
            throw error;
        }
    }

    async function register(payload: RegisterPayload) {
        await userService.register(payload);
    }

    async function logout() {
        await userService.logout();
        user.value = undefined;
        membership.value = null;
    }

    function getCurrentUser() {
        return userService.getCurrentUser();
    }

    function findUserById(userId: User['id']) {
        return userService.findUserById(userId);
    }

    function getUsers() {
        return userService.getUsers();
    }

    async function updateUserInfo(dto: UpdateUser, isAdminEditingAnotherUser = false): Promise<void> {
        const currentUserId = user.value?.id;

        const updatedUser = await userService.updateUser(dto, isAdminEditingAnotherUser);

        if (dto.id === currentUserId) {
            user.value = updatedUser;
        }
    }

    async function becomeMember(dto: Omit<CreateMembership, 'userId'>): Promise<void> {
        if (!user.value) {
            throw new UnauthenticatedError('User must be logged in to become a member');
        }

        try {
            const newMembership = await createMembership({ ...dto, userId: user.value.id });
            if (newMembership) {
                membership.value = newMembership;
            }
        } catch (error) {
            logError(error);
            throw error;
        }
    }

    async function refreshMembershipData(): Promise<void> {
        if (user.value) {
            try {
                membership.value = await getMembership();
            } catch (error) {
                logError(error);
            }
        }
    }

    onMounted(async () => {
        if (user.value == null) {
            try {
                const currentUser = await userService.getCurrentUser();

                if (currentUser) {
                    user.value = currentUser;
                    membership.value = await getMembership();
                    const activeSpan = Sentry.getActiveSpan();

                    if (activeSpan) {
                        activeSpan.setAttribute('user.username', currentUser.username);
                        activeSpan.setAttribute('user.role', currentUser.role);
                    }
                }
            } catch (error) {
                logError(error);
            } finally {
                isLoading.value = false;
            }
        } else {
            isLoading.value = false;
        }

        userPromiseResolver?.value?.();
    });

    // Track auth state changes coming from another tab, token-refresh failures,
    // or session revocation. Without this the in-memory user stays stale until
    // the page is reloaded.
    //
    // Request-id guard: two events landing close together (e.g. SIGNED_IN
    // immediately followed by TOKEN_REFRESHED) used to trigger overlapping
    // getCurrentUser/getMembership fetches; whichever resolved last wins, which
    // might be the older one. Each event bumps the id and stale resolutions are
    // discarded.
    //
    // The async work is deferred via setTimeout so the supabase auth lock
    // (held by whatever call emitted the event — typically setSession during
    // login) is released before we call back into the SDK. Awaiting SDK calls
    // synchronously inside this callback deadlocks: setSession holds the lock,
    // notifyAllSubscribers awaits this callback, getCurrentUser → getUser
    // would re-enter the lock and wait for setSession to finish.
    //
    // Visibility gate: when the tab is hidden, browsers throttle and may
    // freeze in-flight fetches and timers. A TOKEN_REFRESHED kicked off here
    // would issue `getMembership()` (a 20s-timed edge function call); if the
    // tab freezes mid-fetch the request can be parked while
    // `invokeWithTimeout`'s setTimeout eventually resumes and raises a
    // misleading "timed out after 20000ms" Sentry error. Defer the SDK work
    // until the next time the tab is visible.
    //
    // We intentionally don't unsubscribe: Pinia stores are app-lifetime
    // singletons and the Supabase auth subscription is torn down by the
    // browser on page unload anyway. A previous `onUnmounted(unsubscribe)`
    // here was dead code because the store is never disposed during a session.
    let authEventId = 0;

    async function refreshUserAndMembership(id: number) {
        try {
            const refreshed = await userService.getCurrentUser();

            if (id !== authEventId) {
                return;
            }

            if (refreshed) {
                user.value = refreshed;
                const refreshedMembership = await getMembership();

                if (id !== authEventId) {
                    return;
                }

                membership.value = refreshedMembership;
            } else {
                user.value = undefined;
                membership.value = null;
            }
        } catch (error) {
            if (id !== authEventId) {
                return;
            }
            logError(error);
        }
    }

    function runWhenVisible(callback: () => void) {
        if (document.visibilityState === 'visible') {
            callback();
            return;
        }

        const onVisible = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            document.removeEventListener('visibilitychange', onVisible);
            callback();
        };
        document.addEventListener('visibilitychange', onVisible);
    }

    client.auth.onAuthStateChange((event) => {
        const id = ++authEventId;

        if (event === 'SIGNED_OUT') {
            userService.clearCurrentUser();
            user.value = undefined;
            membership.value = null;
            return;
        }

        if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED' || event === 'SIGNED_IN') {
            userService.clearCurrentUser();
            setTimeout(() => {
                runWhenVisible(() => {
                    if (id !== authEventId) {
                        return;
                    }
                    void refreshUserAndMembership(id);
                });
            }, 0);
        }
    });

    watch(membership, (currentMembership, oldMembership) => {
        if (currentMembership == null) {
            return;
        }

        if (oldMembership?.id === currentMembership.id) {
            return;
        }

        const entriesWaiting = currentMembership.gamesWithoutRecord;

        if (entriesWaiting.length === 0) {
            return;
        }

        const count = entriesWaiting.length;
        const first = entriesWaiting[0];
        const message = count > 1
            ? `Potrebno je unijeti volonterske zapise za ${count} odvođenih igara.`
            : `Potrebno je unijeti volonterski zapis za odvođenu igru.`;

        const confirmAction = {
            label: 'Unesi',
            callback: () => router.push({
                name: Route.Game,
                params: {
                    gameId: `${slugify(first.title)}--${first.gameId}`,
                }
            }),
        };

        const cancelAction = {
            label: 'Ne sada',
            callback: () => { /* Nothing to do */ },
        };

        notif.showNotification({
            message,
            type: 'info',
            confirmAction,
            cancelAction,
            duration: 10_000,
        });
    }, { immediate: true });

    return {
        isLoading,
        isLoggedIn,
        isNonProfitMember,
        role,
        currentMembership: readonly(membership),
        isAdmin: computed(() => role.value != null && isAdminRole(role.value)),
        canStartGame: computed(() => role.value != null && (isAdminRole(role.value) || role.value === Role.GM)),
        user,
        isUserResolvedPromise,
        login,
        loginWithDiscord,
        loginWithPasskey,
        becomeMember,
        logout,
        register,
        getCurrentUser,
        findUserById,
        getUsers,
        updateUserInfo,
        resetPassword,
        refreshMembershipData,
    };
});